import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { UsersService } from 'src/app/shared/api-services/users.service';
import { CommunicationService } from 'src/app/shared/utilities/comunication.service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UserModel } from 'src/app/shared/models/user/users';
import { UsersFilter } from 'src/app/shared/models/user/user-filter';
import { TenantService } from 'src/app/shared/api-services/tenant.service';
import { AutocompleteModule } from '../../../inputs/autocomplete-primeng/autocomplete.module';
import { UserGroup } from 'src/app/shared/models/user-group/user-group';

// Interfaccia per tenere traccia del risultato di ogni utente
interface UserResult {
  id: string;
  success: boolean;
  displayName?: string;
  errorMessage?: string;
}

@Component({
    selector: 'user-enable-massive-modal',
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        AutocompleteModule
    ],
    templateUrl: './user-enable-massive-modal.component.html',
    styleUrls: ['./user-enable-massive-modal.component.scss']
})

export class UserEnableMassiveModalComponent implements OnInit, OnDestroy {

  @Input() userIds: string[];
  users: UserModel[];

  form: FormGroup;
  selectedUserGroups: UserGroup[] = [];
  
  // Variabili per la barra di progresso
  onApplyProgressStep = false;
  progressBarValue = 0;
  elementProcessed = 0;
  totalElements = 0;

  // Variabili per il riepilogo dei risultati
  showSummary = false;
  processResults: UserResult[] = [];
  successCount = 0;
  failureCount = 0;

  private _destroy$ = new Subject<void>();

  constructor(
    public bsModalRef: BsModalRef,
    private _usersService: UsersService,
    private _communicationService: CommunicationService,
    private _toastrService: ToastrService,
    private _translateService: TranslateService,
    private _tenantService: TenantService
  ) {
    this.form = new FormGroup({
      userGroupIds: new FormControl([], [Validators.required])
    });
  }

  ngOnInit(): void {
    // Precarica i dettagli degli utenti
    this.getUsers();
  }

  private getUsers() {
    if (this.userIds && this.userIds.length > 0) {
      const userFilter: UsersFilter = {
        idsList: this.userIds.join(',')
      };
      this._usersService.listUsers$(userFilter)
        .pipe(takeUntil(this._destroy$))
        .subscribe(users => {
          this.users = users.items;
        });
    }
  }

  onUserGroupSelected(group: UserGroup): void {
    if (!this.selectedUserGroups.some(g => g.id === group.id)) {
      this.selectedUserGroups.push(group);
      this.updateUserGroupIds();
    }
  }

  removeUserGroup(groupId: string): void {
    this.selectedUserGroups = this.selectedUserGroups.filter(group => group.id !== groupId);
    this.updateUserGroupIds();
  }

  private updateUserGroupIds(): void {
    const userGroupIds = this.selectedUserGroups.map(group => group.id);
    this.form.patchValue({ userGroupIds });
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    // Reset dei risultati
    this.processResults = [];
    this.successCount = 0;
    this.failureCount = 0;
    
    // Attiva la barra di progresso
    this.onApplyProgressStep = true;
    this.totalElements = this.userIds.length;
    this.elementProcessed = 0;
    this.progressBarValue = 0;

    this.processNextUser(0);
  }

  private processNextUser(index: number): void {
    if (index >= this.userIds.length) {
      // Abbiamo finito di processare tutti gli utenti
      this._toastrService.success(this._translateService.instant('USER_ENABLE.SUCCESS_OPERATION'));
      this.showSummary = true;
      this._communicationService.sendEvent();
      return;
    }

    const userId = this.userIds[index];
    
    // Trova l'utente corrente
    const currentUser = this.users?.find(u => u.id === userId);
    
    // Attiva l'utente assegnando i gruppi utente selezionati
    this._usersService.updateUserGroups$(this._tenantService.getTenantId(), userId, this.form.value.userGroupIds)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          // Aggiorna la barra di progresso
          this.elementProcessed++;
          this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
          
          // Registra il successo
          this.processResults.push({
            id: userId,
            success: true,
            displayName: currentUser?.displayName || currentUser?.email || userId
          });
          this.successCount++;
          
          // Procedi con il prossimo utente
          this.processNextUser(index + 1);
        },
        error: (err) => {
          // Aggiorna la barra di progresso in caso di errore
          this.elementProcessed++;
          this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
          
          // Registra il fallimento
          this.processResults.push({
            id: userId,
            success: false,
            displayName: currentUser?.displayName || currentUser?.email || userId,
            errorMessage: err?.error?.message || this._translateService.instant('ERRORS.GENERIC')
          });
          this.failureCount++;
          
          // Procedi con il prossimo utente
          this.processNextUser(index + 1);
        }
      });
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
} 